/**
 * InternalColumnsRow — read-only bucket for CSV columns whose header matches a
 * property Ahoy maintains automatically (system-managed fields such as
 * last-activity timestamps and other rollups). These can't be set during
 * import, so they are surfaced here — informational, never draggable or
 * assignable — instead of landing in the "Unassigned columns" bucket, which
 * implies the user should act on them.
 */

import { HelpCircle } from '@untitledui/icons';

import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip';

const INTERNAL_COLUMNS_TOOLTIP =
  "These columns match fields Ahoy maintains automatically (for example, last-activity timestamps and other rollups). They can't be set during import, so they're skipped – the rest of each row still imports.";

interface InternalColumnsRowProps {
  /** Columns whose header matches a system-managed property (cannot be imported). */
  columns: string[];
}

export function InternalColumnsRow({ columns }: InternalColumnsRowProps) {
  if (columns.length === 0) {
    return null;
  }

  return (
    <div className='rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] px-4 py-3'>
      <div className='flex items-center gap-1.5'>
        <span className='text-sm font-medium text-[var(--color-text-secondary)]'>
          Internal columns
        </span>
        <span className='text-xs text-[var(--color-text-tertiary)]'>
          ({columns.length}) – managed by Ahoy
        </span>
        <Tooltip title={INTERNAL_COLUMNS_TOOLTIP} placement='top'>
          <TooltipTrigger
            aria-label='Why internal columns cannot be imported'
            className='text-fg-quaternary hover:text-fg-tertiary cursor-default'
          >
            <HelpCircle className='w-3.5 h-3.5' />
          </TooltipTrigger>
        </Tooltip>
      </div>
      {/* A list, not a table: one column of names with no tabular relationship
          to convey. `lib/base/Table` would wrap this in a role="grid" with cell
          navigation, which is more to hear and no more to learn. */}
      <ul className='mt-2 w-full'>
        {columns.map(column => (
          <li
            key={column}
            className='border-b border-[var(--color-border-secondary)] py-1.5 text-sm text-[var(--color-text-primary)] last:border-b-0'
          >
            {column}
          </li>
        ))}
      </ul>
    </div>
  );
}
